import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const PIN_LENGTH = 6;

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'] as const;

export interface PinPadProps {
  readonly onComplete: (pin: string) => void;
  readonly disabled?: boolean;
  /** Trigger a horizontal shake animation (e.g. on wrong PIN). */
  readonly shake?: boolean;
}

export function PinPad({ onComplete, disabled, shake = false }: PinPadProps) {
  const [pin, setPin] = useState('');
  const reducedMotion = useReducedMotion();
  const shakeAnimation =
    shake && !reducedMotion ? { x: [-6, 6, -4, 4, -2, 0] } : { x: 0 };

  const append = (digit: string) => {
    if (disabled) return;
    if (pin.length < PIN_LENGTH) {
      const next = pin + digit;
      setPin(next);
      if (next.length === PIN_LENGTH) {
        onComplete(next);
        setPin('');
      }
    }
  };

  const del = () => {
    if (disabled) return;
    setPin(pin.slice(0, -1));
  };

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-8">
      <motion.div
        animate={shakeAnimation}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        className="flex gap-5 py-3"
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span
            key={i}
            className={`h-5 w-5 rounded-full border-2 border-text-primary transition-colors ${
              i < pin.length ? 'bg-text-primary' : 'bg-transparent'
            }`}
          />
        ))}
      </motion.div>
      <div className="grid w-full grid-cols-3 gap-3">
        {KEYS.map((k) => {
          if (k === '') {
            return <div key="blank" className="aspect-[1.5/1]" />;
          }
          if (k === 'del') {
            return (
              <button
                key="del"
                type="button"
                onClick={del}
                disabled={disabled || pin.length === 0}
                className="flex aspect-[1.5/1] items-center justify-center rounded-2xl bg-transparent text-base font-medium text-link transition-colors duration-fast ease-press active:scale-[0.97] disabled:opacity-40"
                aria-label="backspace"
              >
                ⌫
              </button>
            );
          }
          return (
            <button
              key={k}
              type="button"
              onClick={() => append(k)}
              disabled={disabled}
              className="flex aspect-[1.5/1] items-center justify-center rounded-2xl bg-surface-2 text-[26px] font-medium text-text-primary transition-colors duration-fast ease-press hover:bg-surface-3 active:scale-[0.97] disabled:opacity-40"
            >
              {k}
            </button>
          );
        })}
      </div>
    </div>
  );
}
